import { PlusIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGetAgentChatThreadQueryKey,
  getListAgentChatThreadsQueryKey,
  useDeleteAgentChatThread,
  useListAgentChatThreads,
  useToggleAgentChatThreadFavorite,
  useUpdateAgentChatThread,
} from '@/entities/project/api/generated/api';
import type { ProjectAgentChatThreadSchema } from '@/entities/project/api/generated/model';
import { Button, Dialog, Separator, Text, toast } from '@/shared/ui';
import { Input } from '@/shared/ui/form/input/Input';
import { CHAT_HISTORY_PAGE_SIZE } from '@/widgets/right-side-menu/lib/chat-history-constants';
import type { AIContextMenu } from '@/widgets/right-side-menu/lib/types';
import { ChatHistoryItem } from './ChatHistoryItem';

type Props = {
  projectId: string;
  setAiContextMenu: (context: AIContextMenu) => void;
  onNewChat?: () => void;
};

export const ChatHistoryList: FC<Props> = ({ projectId, setAiContextMenu, onNewChat }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // 履歴一覧取得
  const { data, isLoading } = useListAgentChatThreads(projectId, {
    page_size: CHAT_HISTORY_PAGE_SIZE,
  });

  const threads = data?.items ?? [];
  const favorites = threads.filter((thread) => thread.is_favorite);
  const recentThreads = threads.filter((thread) => !thread.is_favorite);

  // 削除
  const [threadToDelete, setThreadToDelete] = useState<Pick<
    ProjectAgentChatThreadSchema,
    'id' | 'title'
  > | null>(null);

  const deleteMutation = useDeleteAgentChatThread({
    mutation: {
      onSuccess: (_data, variables) => {
        toast.success(t('common.messages.delete-success', { name: t('ai.chat-history.title') }));
        queryClient.invalidateQueries({
          queryKey: getListAgentChatThreadsQueryKey(projectId),
        });
        // 削除されたスレッドの詳細キャッシュも除去
        queryClient.removeQueries({
          queryKey: getGetAgentChatThreadQueryKey(projectId, variables.threadId),
        });
      },
      onError: () => {
        toast.error(t('common.messages.delete-failed', { name: t('ai.chat-history.title') }));
      },
    },
  });

  const handleDeleteConfirm = () => {
    if (threadToDelete) {
      deleteMutation.mutate({ projectId, threadId: threadToDelete.id });
      setThreadToDelete(null);
    }
  };

  // 名前変更
  const [threadToRename, setThreadToRename] = useState<Pick<
    ProjectAgentChatThreadSchema,
    'id' | 'title'
  > | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const updateMutation = useUpdateAgentChatThread({
    mutation: {
      onSuccess: () => {
        toast.success(t('common.messages.update-success', { name: t('ai.chat-history.title') }));
        queryClient.invalidateQueries({
          queryKey: getListAgentChatThreadsQueryKey(projectId),
        });
      },
      onError: () => {
        toast.error(t('common.messages.update-failed', { name: t('ai.chat-history.title') }));
      },
    },
  });

  const handleRenameConfirm = () => {
    if (threadToRename && renameValue.trim()) {
      updateMutation.mutate({
        projectId,
        threadId: threadToRename.id,
        data: { title: renameValue.trim() },
      });
      setThreadToRename(null);
      setRenameValue('');
    }
  };

  const handleOpenRename = (thread: Pick<ProjectAgentChatThreadSchema, 'id' | 'title'>) => {
    setThreadToRename(thread);
    setRenameValue(thread.title);
  };

  // お気に入り切替
  const favoriteMutation = useToggleAgentChatThreadFavorite({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListAgentChatThreadsQueryKey(projectId),
        });
      },
      onError: () => {
        toast.error(t('common.messages.update-failed', { name: t('ai.chat-history.title') }));
      },
    },
  });

  const handleToggleFavorite = (thread: ProjectAgentChatThreadSchema) => {
    favoriteMutation.mutate({
      projectId,
      threadId: thread.id,
      data: { is_favorite: !thread.is_favorite },
    });
  };

  // スレッド選択
  const handleSelectThread = (threadId: string) => {
    setAiContextMenu({ route: 'projectDetail', mode: 'agent_chat', threadId });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1 py-1">
        <div className="flex h-9 items-center px-3">
          <div className="h-4 w-32 animate-pulse rounded bg-surface-secondary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col py-1">
      {/* 新しいチャットボタン（固定） */}
      <div className="shrink-0 px-1">
        <Button variant="ghost" className="h-9 w-full justify-start" onClick={onNewChat}>
          <PlusIcon className="size-4" />
          <Text as="span">{t('ai.chat-history.new-chat')}</Text>
        </Button>
      </div>

      {/* スクロール可能なリスト領域 */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <Text variant="description" className="px-3 py-4 text-center">
            {t('ai.chat-history.empty')}
          </Text>
        ) : (
          <>
            {/* お気に入りセクション */}
            {favorites.length > 0 && (
              <>
                <Separator className="my-1" />
                <div className="flex flex-col">
                  <Text
                    variant="description"
                    className="sticky top-0 z-10 mt-2 mb-1 bg-surface-base px-3 py-1"
                  >
                    {t('ai.chat-history.section-favorites')}
                  </Text>
                  {favorites.map((thread) => (
                    <ChatHistoryItem
                      key={thread.id}
                      title={thread.title}
                      isFavorite
                      onClick={() => handleSelectThread(thread.id)}
                      onRename={() => handleOpenRename(thread)}
                      onToggleFavorite={() => handleToggleFavorite(thread)}
                      onDelete={() => setThreadToDelete(thread)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* 最近セクション */}
            {recentThreads.length > 0 && (
              <>
                <Separator className="my-1" />
                <div className="flex flex-col">
                  <Text
                    variant="description"
                    className="sticky top-0 z-10 mt-2 mb-1 bg-surface-base px-3 py-1"
                  >
                    {t('ai.chat-history.section-recent')}
                  </Text>
                  {recentThreads.map((thread) => (
                    <ChatHistoryItem
                      key={thread.id}
                      title={thread.title}
                      isFavorite={false}
                      onClick={() => handleSelectThread(thread.id)}
                      onRename={() => handleOpenRename(thread)}
                      onToggleFavorite={() => handleToggleFavorite(thread)}
                      onDelete={() => setThreadToDelete(thread)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={threadToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setThreadToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmVariant="destructive"
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        title={t('common.messages.delete-confirm-title', {
          name: t('ai.chat-history.title'),
        })}
      >
        <Text>
          {t('common.messages.delete-confirm-message', { itemName: threadToDelete?.title })}
          <br />
          {t('common.messages.delete-confirm-warning')}
        </Text>
      </Dialog>

      {/* 名前変更ダイアログ */}
      <Dialog
        open={threadToRename !== null}
        onOpenChange={(open) => {
          if (!open) {
            setThreadToRename(null);
            setRenameValue('');
          }
        }}
        onConfirm={handleRenameConfirm}
        confirmLabel={t('common.actions.save')}
        cancelLabel={t('common.actions.cancel')}
        disabled={!renameValue.trim()}
        title={t('ai.chat-history.rename-dialog-title')}
        width="s"
      >
        <Input
          autoComplete={false}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder={t('ai.chat-history.rename-dialog-placeholder')}
          maxLength={255}
        />
      </Dialog>
    </div>
  );
};
